import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { getErrorStatus, shouldRetryError } from '@/utils/errorHandling'
import { notifyError } from '../error/errorNotifier'
import { errorService } from '../error/errorService'

/**
 * Default stale time for queries (5 minutes)
 * This determines how long data is considered fresh
 */
const DEFAULT_STALE_TIME = 1000 * 60 * 5

/**
 * Default cache time for queries (1 hour)
 * This determines how long data is kept in cache after becoming inactive
 */
export const DEFAULT_CACHE_TIME = 1000 * 60 * 60

/**
 * Creates a configured QueryClient instance for React Query
 * @param {object} options - Override options for the query client
 * @returns {QueryClient} Configured QueryClient instance
 */
export function createQueryClient(options = {}) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only log errors that weren't explicitly thrown by a query
        if (!query.meta?.skipErrorLogging) {
          errorService.logError(error, {
            context: 'React Query',
            queryKey: query.queryKey,
          })
        }
        // Queries opt into a user-facing toast by declaring meta.errorTitle;
        // meta.silentStatuses lists HTTP statuses the page already renders inline
        const silenced = query.meta?.silentStatuses?.includes(getErrorStatus(error))
        if (query.meta?.errorTitle && !silenced) {
          notifyError(error, { title: query.meta.errorTitle })
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        if (!mutation.meta?.skipErrorLogging) {
          errorService.logError(error, {
            context: 'Mutation',
            variables,
          })
        }
        if (mutation.meta?.errorTitle) {
          notifyError(error, { title: mutation.meta.errorTitle })
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_CACHE_TIME,
        // Disable refetch on window focus to prevent excessive API calls
        // Individual queries can override this if needed
        refetchOnWindowFocus: false,
        // Disable refetch on reconnect by default (can be overridden per query)
        refetchOnReconnect: false,
        // Don't refetch on mount if data exists in cache (prevents duplicate API calls)
        // Individual queries can override this if needed
        refetchOnMount: false,
        // Single retry policy for the whole app; the fetch layer does not retry
        retry: shouldRetryError,
        ...options.queries,
      },
      mutations: {
        ...options.mutations,
      },
    },
  })
}

/**
 * Default query client instance for the application
 */
export const queryClient = createQueryClient()
